<template>
  <router-view :key="$route.fullPath"></router-view>

  <!-- Global Access-Denied banner (fires on any 403 with category_code) -->
  <transition name="ad-fade">
    <div v-if="accessDenied.visible" class="ad-overlay" @click.self="dismiss">
      <div class="ad-card">
        <div class="ad-icon"><i class="mdi mdi-shield-lock-outline"></i></div>
        <h5 class="ad-title">Access Denied</h5>
        <p class="ad-msg">{{ accessDenied.message || defaultMsg }}</p>
        <div class="ad-detail">
          <div><span>Data category:</span> <code>{{ accessDenied.category }}</code></div>
          <div><span>Required:</span> <code>{{ accessDenied.required }}</code></div>
          <div><span>You have:</span> <code>{{ accessDenied.granted || 'none' }}</code></div>
          <div><span>Your role:</span> <code>{{ myRole || 'unknown' }}</code></div>
        </div>

        <!-- Success state after request submitted -->
        <div v-if="requestSubmitted" class="ad-success">
          <i class="mdi mdi-check-circle me-1"></i>
          Request submitted. A Platform Admin will review it and you'll get access
          the moment it's approved. Refresh this page after approval.
        </div>

        <!-- Reason form -->
        <div v-else>
          <div class="ad-guide">
            Your role does not permit access to this data. Explain briefly why you need it —
            a Platform Admin will see this in their console and approve or reject.
          </div>
          <label class="ad-label">Reason (optional but recommended)</label>
          <textarea v-model="reason" class="ad-textarea" rows="3"
                    placeholder="e.g. Registering a walk-in patient at reception; need to view demographics."></textarea>
          <div v-if="requestError" class="ad-error">
            <i class="mdi mdi-alert-circle me-1"></i> {{ requestError }}
          </div>
        </div>

        <div class="ad-actions">
          <button v-if="!requestSubmitted" class="ad-btn ad-btn-primary" @click="requestAccess" :disabled="submitting">
            <i class="mdi mdi-shield-key-outline me-1"></i>
            {{ submitting ? 'Submitting…' : 'Request access' }}
          </button>
          <button class="ad-btn ad-btn-outline" @click="goToProfile">
            <i class="mdi mdi-account-circle me-1"></i> My profile
          </button>
          <button class="ad-btn ad-btn-plain" @click="dismiss">
            {{ requestSubmitted ? 'Close' : 'Dismiss' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/* eslint-disable */
import { accessDenied, dismissAccessDenied } from "@/access-denied";
import { executeJson, hms } from "@/api";

export default {
  name: "App",
  data() {
    return {
      accessDenied,
      defaultMsg: "Your account is not permitted to view this information.",
      reason: "",
      submitting: false,
      requestSubmitted: false,
      requestError: ""
    };
  },
  watch: {
    "accessDenied.triggeredAt"() {
      // Reset form each time a new denial fires
      this.reason = "";
      this.requestSubmitted = false;
      this.requestError = "";
      this.submitting = false;
    }
  },
  computed: {
    myRole() {
      try { return JSON.parse(localStorage.getItem("userProfile") || "{}").role_code || ""; }
      catch (_) { return ""; }
    },
    myEmail() {
      try { return JSON.parse(localStorage.getItem("userProfile") || "{}").email || ""; }
      catch (_) { return ""; }
    }
  },
  methods: {
    dismiss() { dismissAccessDenied(); },
    goToProfile() { dismissAccessDenied(); this.$router.push("/profile"); },
    async requestAccess() {
      this.submitting = true;
      this.requestError = "";
      try {
        const { data } = await executeJson({
          function:       "submitAccessRequest",
          category_code:  this.accessDenied.category,
          required_level: this.accessDenied.required || "view",
          current_level:  this.accessDenied.granted  || "none",
          reason:         this.reason
        }, hms);
        if (data && data.success) {
          this.requestSubmitted = true;
        } else {
          this.requestError = (data && data.message) || "Could not submit the request.";
        }
      } catch (e) {
        this.requestError = "Connection error. Please try again.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.ad-overlay {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 5000;
  padding-top: 90px;
  backdrop-filter: blur(2px);
}
.ad-card {
  background: #fff;
  width: 480px; max-width: calc(100% - 40px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.25);
  padding: 24px 28px;
  border-top: 4px solid #dc3545;
}
.ad-icon {
  width: 48px; height: 48px; border-radius: 50%;
  background: #fef2f2; color: #dc3545;
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; margin-bottom: 12px;
}
.ad-title { font-size: 18px; font-weight: 700; margin: 0 0 6px; color: #0f172a; }
.ad-msg   { font-size: 14px; color: #334155; margin: 0 0 14px; }
.ad-detail {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 10px 12px; font-size: 12.5px; color: #475569; margin-bottom: 14px;
}
.ad-detail > div { margin: 3px 0; display: flex; gap: 8px; }
.ad-detail span { color: #64748b; min-width: 100px; }
.ad-detail code { background: #eef2f7; padding: 1px 6px; border-radius: 4px; color: #0f172a; }
.ad-guide { font-size: 12.5px; color: #64748b; margin-bottom: 10px; line-height: 1.55; }
.ad-label { font-size: 11.5px; font-weight: 600; color: #475569; letter-spacing: 0.3px; text-transform: uppercase; display: block; margin-bottom: 4px; }
.ad-textarea {
  width: 100%; border: 1px solid #cbd5e1; border-radius: 8px;
  padding: 8px 10px; font-size: 13px; resize: vertical; margin-bottom: 12px;
  font-family: inherit;
}
.ad-textarea:focus { outline: none; border-color: #1a7fe8; box-shadow: 0 0 0 3px rgba(26,127,232,0.15); }
.ad-error { color: #dc3545; font-size: 12.5px; margin-bottom: 12px; }
.ad-success {
  background: #ecfdf5; border: 1px solid #a7f3d0; color: #065f46;
  padding: 10px 12px; border-radius: 8px; font-size: 13px; margin-bottom: 14px; line-height: 1.5;
}
.ad-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.ad-btn {
  padding: 8px 14px; border-radius: 8px; border: none; cursor: pointer;
  font-size: 13px; font-weight: 600; display: inline-flex; align-items: center;
}
.ad-btn-primary { background: #1a7fe8; color: #fff; }
.ad-btn-primary:hover { background: #1666bd; }
.ad-btn-outline { background: #fff; color: #1a7fe8; border: 1px solid #1a7fe8; }
.ad-btn-outline:hover { background: #eff6ff; }
.ad-btn-plain { background: transparent; color: #64748b; margin-left: auto; }
.ad-btn-plain:hover { color: #0f172a; }

.ad-fade-enter-active, .ad-fade-leave-active { transition: opacity .18s ease; }
.ad-fade-enter-from, .ad-fade-leave-to { opacity: 0; }
</style>
